import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ResearchPage.css';
import {IoMdSend } from 'react-icons/io';
import ChatInputField from '../../ui/components/ChatInputField';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
const isMock = process.env.REACT_APP_MOCK === 'true';

interface ChatData {
  query: string;
  botResponse: BotResponse;
  chatMessages: ChatMessage[];
  chatId: number;
}

interface ChatMessage {
  role: string;
  text: string;
}


interface BotResponse {
  chatText: string;
}

const ResearchPage: React.FC = () => {

  const token = localStorage.getItem('token');
  const nav = useNavigate();
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [chatData, setChatData] = useState<ChatData>({
    query: '',
    botResponse: { chatText: ''},
    chatMessages: [],
    chatId: -1,
  });
  const [topicName, setTopicName] = useState<string>('');
  const [table, setTable] = useState<string>('');
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);


  // And ensure packages load on mount (you already have this):
useEffect(() => {
  getValidPackages();
}, []);


  const startWebSocket = (callback?: (socket?: WebSocket) => void) => {
    // Close the previous WebSocket if it's already open
    const mockEndpoint = isMock ? '/mock' : '';

    if (ws) {
      ws.close();
      setWs(null);
    }

    // Initialize the WebSocket connection
    let socket: WebSocket;
    if (process.env.REACT_APP_ENV === 'local') {
      socket = new WebSocket(`ws://localhost:3000/chatbot${mockEndpoint}`);
    } else {
      socket = new WebSocket(
        `${window.location.origin.replace(/^http/, 'ws')}/chatbot${mockEndpoint}`
      );
    }

    // Set the WebSocket on open connection and call the callback if provided
    socket.onopen = () => {
      console.log('WebSocket opened');
      if (token) {
        console.log('Sending auth token');
        socket.send(JSON.stringify({ type: 'auth', token })); // Send the token as the first message
      }
      setWs(socket);
      if (callback) {
        console.log('Executing callback');
        callback(socket); // Send query only when WebSocket is open, pass socket directly
      }
    };

    // Handle incoming messages
    socket.onmessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      switch (data.type) {
        case 'chatId':
          setChatData((prev) => ({
            ...prev,
            chatId: data.id!,
          }));
          break;
        case 'aiResponseChunk':
          const newTextChunk: string = data.text;
          setChatData((prev) => ({
            ...prev,
            botResponse: {
              ...prev.botResponse,
              chatText: prev.botResponse.chatText + newTextChunk,
            },
          }));
          break;
          case 'messageEnd':
            setChatData((prev) => {
              const aiResponse = prev.botResponse.chatText;
              
              // Set the table content
              setTable(aiResponse);
              
              return {
                ...prev,
                botResponse: { chatText: '' },
                chatMessages: [
                  ...prev.chatMessages,
                  {
                    role: 'system',
                    text: aiResponse,
                  },
                ],
              };
            });
            break;
        case 'noSources':
          setChatData((prev) => ({
            ...prev,
            botResponse: { ...prev.botResponse, sources: [] },
          }));
          break;
        case 'aiSources':
          if (data.sources) {
            try {
              setChatData((prev) => ({
                ...prev,
                botResponse: { ...prev.botResponse},
              }));
            } catch (error) {
              // Optionally handle error
            }
          }
          break;
        case 'inputError':
          console.log(`an error occured with inputs`);
          // TODO prompt user that an error occured and to adjust inputs?
          break;
        case 'serverError':
          console.log(`an error occured with the server`);
          // TODO Tell user server had an issue and maybe try again later
          break;
        case 'authError':
          nav('/signin');
          break;
        default:
          console.error('Unknown message type:', data.type);
          console.log(data);
      }
    };

    socket.onerror = (error: Event) => {
      console.error('WebSocket error:', error);
    };

    // Cleanup WebSocket connection on component unmount
    return () => {
      socket.close();
    };
  };

  const handleSubmit = async () => {
    console.log('=== HANDLE SUBMIT START ===');
    if (!topicName.trim()) return;

    if (ws && ws.readyState === WebSocket.OPEN && topicName != '') {
      console.log('Sending query directly');
      sendQuery();
    } else {
      console.log('Starting WebSocket with callback'); 
      startWebSocket(sendQuery);
    }
  };

  const handleTopicChange = (event: string) => {
    setTopicName(event);
  }

  const sendQuery = async (socket?: WebSocket) => {    
    const model = 'deepseek-chat';
    const prompt = `For ${topicName}, can you create a study guide table that contains rows from the most foundational and easy concepts to master to most difficult concepts to master in a succession. Don't give any introductory information, if something is important add it in the table.`
    
    console.log('=== SEND QUERY START ===');
    console.log('selectedPackages before sending:', selectedPackages); // DEBUG
    
    // Use the provided socket or fall back to the state socket
    const activeSocket = socket || ws;
    console.log('activeSocket exists:', !!activeSocket);
    console.log('activeSocket.readyState:', activeSocket?.readyState);
    console.log('WebSocket.OPEN:', WebSocket.OPEN);
    
    if (activeSocket && activeSocket.readyState === WebSocket.OPEN) {
      console.log('WebSocket is ready, sending payload...');
      const payload = {
        modelType: model,
        query: prompt,
        chatId: chatData.chatId === -1 ? null : chatData.chatId,
        educationLevel: 'University student', // or whatever default you want
      detailLevel: 'Medium', // 'Short', 'Medium', or 'Long'
      threshold: 40, // default threshold value
        selectedPackages: selectedPackages,
      };
      console.log('Final payload:', payload);
      setChatData((prev) => ({
        ...prev,
        query: '',
        botResponse: { chatText: '' },
        chatMessages: [
          ...prev.chatMessages,
          { role: 'user', text: prompt},
        ],
      }));
      activeSocket.send(JSON.stringify(payload));
    } else {
      console.log('WebSocket not ready or not open, skipping send');
    }
  };

  const getValidPackages = async () => {
    console.log('getValidPackages called');
    const url = '/validpackages';
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
      });
      if (!response.ok) {
        if (response.status === 401) {
          nav('/signin');
        }
        throw new Error(`Network response was not ok`);
        // TODO should set a variable here
      }
      const json = await response.json();
      const packages: string[] = json;
      console.log('API returned packages:', packages);
    console.log('Setting selectedPackages to:', [packages[0]]);
      setSelectedPackages([packages[0]]); // TODO Critical, when updating with actual selection capability remove this.
      console.log('After setSelectedPackages, current selectedPackages:', selectedPackages); // This will show old state!
      return packages; 
    } catch (error) {
      // TODO should set a variable here
    }
  };

  // Also add this to see when selectedPackages actually updates:
useEffect(() => {
  console.log('selectedPackages state changed to:', selectedPackages);
}, [selectedPackages]);

  return (
    <div className="main-chat-container">
          <div className="primaryChatContainer">
            {/* <div className="ChatPageMainWrapper">
              <div className="ChatPageChatArea">
                {chatData.chatMessages.length === 0 &&
                !chatData.botResponse.chatText ? (
                  <div className="defaultScreen">
                    <img
                      src={'/video-search_Icon.png'}
                      alt="Logo"
                      className="logo"
                    />
                    <h4 className="defaultScreenName">Video Search AI</h4>
                  </div>
                ) : (
                  <div className="ChatScrollArea">
                    {chatData.chatMessages.map((msg, i) => (
                      <div key={i} className={`chat-message ${msg.role}`}>
                        {msg.role === 'system' ? (
                          <div
                            className="system-message-layout"
                          >
                            <div className="message-content-with-button">
                              <div className="message-content-right">
                                {msg.text}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="message-content">{msg.text}</div>
                        )}
                      </div>
                    ))}
                    {chatData.botResponse.chatText != '' && (
                      <div className="chat-message system">
                        <div
                          className="system-message-layout"
                        >
                          <div className="message-content-with-button">
                            <div className="message-content-right">
                              {chatData.botResponse.chatText}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div> */}



            <div className="ChatSearchContainer">
              <div className="ChatPageQuery">
                <ChatInputField
                  value={topicName}
                  label={'Enter topic name...'}
                  onValueChange={handleTopicChange}
                  onEnterPress={handleSubmit}
                ></ChatInputField>
                <IoMdSend
                  onClick={handleSubmit}
                  className="ChatPageConfirmButton"
                />
              </div>
            </div>

            {table && (
              <div className="generated-table-container">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {table}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>
  );
};

export default ResearchPage;
